import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import './GlobalPopup.scss';

interface PopupState {
  isVisible: boolean;
  content: string;
  x: number;
  y: number;
}

interface PopupContextType {
  showPopup: (content: string, event: React.MouseEvent) => void;
  hidePopup: () => void;
  updatePosition: (event: React.MouseEvent) => void;
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export const usePopup = (): PopupContextType => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};

interface PopupProviderProps {
  children: React.ReactNode;
}

export const PopupProvider: React.FC<PopupProviderProps> = ({ children }) => {
  const [popupState, setPopupState] = useState<PopupState>({
    isVisible: false,
    content: '',
    x: 0,
    y: 0,
  });

  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const forceHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setPopupState(prev => ({ ...prev, isVisible: false }));
  }, []);

  const showPopup = useCallback((content: string, event: React.MouseEvent) => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setPopupState({
      isVisible: true,
      content,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  }, []);

  const hidePopup = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setPopupState(prev => ({ ...prev, isVisible: false }));
    }, 100);
  }, []);

  const updatePosition = useCallback((event: React.MouseEvent) => {
    if (popupState.isVisible) {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      setPopupState(prev => ({
        ...prev,
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      }));
    }
  }, [popupState.isVisible]);

  useEffect(() => {
    const handleClick = () => {
      forceHide();
    };

    const handleScroll = () => {
      forceHide();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || 
          event.key === 'Enter' || 
          event.key === 'Tab' ||
          event.key === 'ArrowUp' ||
          event.key === 'ArrowDown' ||
          event.key === 'ArrowLeft' ||
          event.key === 'ArrowRight') {
        forceHide();
      }
    };

    const handleBeforeUnload = () => {
      forceHide();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        forceHide();
      }
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [forceHide]);

  useEffect(() => {
    const handlePopState = () => {
      forceHide();
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [forceHide]);

  const contextValue: PopupContextType = useMemo(() => ({
    showPopup,
    hidePopup,
    updatePosition,
  }), [showPopup, hidePopup, updatePosition]);

  return (
    <PopupContext.Provider value={contextValue}>
      {children}
      {popupState.isVisible && (
        <div
          className="global-popup"
          style={{
            left: popupState.x,
            top: popupState.y,
          }}
        >
          {popupState.content}
        </div>
      )}
    </PopupContext.Provider>
  );
};